import type {
    CrossWindowChannel,
    DownloadRepositoryArchiveMessage,
    DownloadRepositoryArchiveResultMessage,
  } from '@gitlab/cross-origin-channel';
  import type { WebIdeConfig } from '@gitlab/web-ide-types';
  
  type Phase = DownloadRepositoryArchiveResultMessage['params']['phase'];
  
  /**
   * Pull the filename out of a Content-Disposition response header.
   * Supports the legacy `filename="..."` form and RFC 5987's
   * `filename*=UTF-8''...` form.
   */
  const parseFilenameFromContentDisposition = (
    header: string | null,
  ): string | undefined => {
    if (!header) return undefined;
  
    const star = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header);
    if (star?.[1]) {
      try {
        return decodeURIComponent(star[1].trim());
      } catch {
        /* fall through */
      }
    }
  
    const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
    return plain?.[1]?.trim();
  };
  
  /**
   * Save a Blob to a location chosen by the user.
   * @returns 'completed' if saved, 'cancelled' if user dismissed picker.
   */
  const saveBlobToUserChosenLocation = async (
    blob: Blob,
    suggestedName: string,
  ): Promise<'completed' | 'cancelled'> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyWindow = window as any;
  
    if (typeof anyWindow.showSaveFilePicker === 'function') {
      try {
        const handle = await anyWindow.showSaveFilePicker({
          suggestedName,
          types: [
            {
              description: 'ZIP archive',
              accept: { 'application/zip': ['.zip'] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return 'completed';
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((err as any)?.name === 'AbortError') {
          return 'cancelled';
        }
        // Fall through to anchor-based fallback for non-cancellation errors.
      }
    }
  
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = suggestedName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
    return 'completed';
  };
  
  /**
   * Handle a `download-repository-archive` message from the extension worker.
   *
   * The worker has no DOM and can't trigger a save dialog itself, so it
   * delegates here. We post `download-repository-archive-result` messages
   * back at each phase so the worker's progress notification can mirror
   * the actual work being done in this frame.
   */
  export const handleDownloadRepositoryArchiveMessage = async (
    config: WebIdeConfig,
    message: DownloadRepositoryArchiveMessage,
    windowChannel: CrossWindowChannel,
  ): Promise<void> => {
    const { ref, requestId } = message.params;
  
    // why: Defensive guard. If a stale build leaves the dispatcher in
    // `index.ts` calling this with the old 2-arg signature, `windowChannel`
    // arrives as `undefined` and every `postResult` blows up with the
    // cryptic "Cannot read properties of undefined (reading 'postMessage')".
    // Surface the real cause instead, and degrade to a window.alert so the
    // user at least sees an error rather than a silent failure.
    if (!windowChannel || typeof windowChannel.postMessage !== 'function') {
      // eslint-disable-next-line no-alert, no-console
      console.error(
        '[handleDownloadRepositoryArchiveMessage] windowChannel argument is missing — likely a stale Vite cache. Try `rm -rf node_modules/.vite` and rebuild.',
      );
      // eslint-disable-next-line no-alert
      window.alert(
        'Repository download could not start: the IDE iframe and the extension are out of sync. Please reload the page; if the issue persists, the developer needs to clear the build cache and rebuild.',
      );
      return;
    }
  
    const postResult = (
      phase: Phase,
      extras: { suggestedName?: string; error?: string } = {},
    ) => {
      windowChannel.postMessage({
        key: 'download-repository-archive-result',
        params: { requestId, phase, ...extras },
      });
    };
  
    // The Unify-specific fields are not on the published WebIdeConfig type
    // but are present on the runtime object. Mirrors the cast pattern used
    // in TokenService.ts and deploy.ts.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { funnel_id, webServiceUrl, jwt_token } = config as any;
    const { projectPath } = config;
  
    if (!funnel_id || !webServiceUrl || !jwt_token) {
      postResult('failed', {
        error:
          'Unify webService configuration is missing (funnel_id, webServiceUrl, or jwt_token).',
      });
      return;
    }
  
    if (!ref) {
      postResult('failed', { error: 'Missing branch ref.' });
      return;
    }
  
    const baseUrl = webServiceUrl.endsWith('/')
      ? webServiceUrl.slice(0, -1)
      : webServiceUrl;
    const archiveUrl =
      `${baseUrl}/api/framework/funnel/${encodeURIComponent(funnel_id)}` +
      `/repository/archive.zip?sha=${encodeURIComponent(ref)}`;
  
    postResult('fetching');
  
    let response: Response;
    try {
      response = await fetch(archiveUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${jwt_token}`,
          Accept: 'application/octet-stream, application/zip;q=0.9, */*;q=0.8',
        },
      });
    } catch (err) {
      postResult('failed', {
        error: `Network error reaching webService: ${
          err instanceof Error ? err.message : String(err)
        }`,
      });
      return;
    }
  
    if (!response.ok) {
      let detail = `${response.status} ${response.statusText}`;
      try {
        const errBody = await response.text();
        if (errBody) {
          try {
            const parsed = JSON.parse(errBody);
            if (Array.isArray(parsed?.errors) && parsed.errors.length) {
              detail = parsed.errors.join(', ');
            } else {
              detail = `${detail} — ${errBody}`;
            }
          } catch {
            detail = `${detail} — ${errBody}`;
          }
        }
      } catch {
        /* ignore body-read errors */
      }
      postResult('failed', { error: detail });
      return;
    }
  
    const blob = await response.blob();
  
    const projectName =
      (projectPath && projectPath.split('/').pop()) || 'repository';
    const suggestedName =
      parseFilenameFromContentDisposition(
        response.headers.get('Content-Disposition'),
      ) ?? `${projectName}-${ref}.zip`;
  
    postResult('saving', { suggestedName });
  
    try {
      const outcome = await saveBlobToUserChosenLocation(blob, suggestedName);
      postResult(outcome, { suggestedName });
    } catch (err) {
      postResult('failed', {
        error: `Failed to save file: ${
          err instanceof Error ? err.message : String(err)
        }`,
      });
    }
  };